import { forwardRef, useContext } from 'react';
import type {
  BorderProps,
  ResponsiveValue,
  TypographyProps,
} from 'styled-system';
import { border, typography } from 'styled-system';

import type {
  BorderOverrideProps,
  TextDecorationProps,
  TextRenderingProps,
  TextTransformProps,
  UserSelectProps,
} from '../extensions';
import {
  borderShorthandOverride,
  textDecoration,
  textRendering,
  textTransform,
  userSelect,
} from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';
import ListContext from './ListContext';

export interface SystemListProps
  extends Omit<BorderProps, keyof BorderOverrideProps>,
    TextDecorationProps,
    TextRenderingProps,
    TextTransformProps,
    TypographyProps,
    UserSelectProps,
    BorderOverrideProps {
  variant?: ResponsiveValue<string>;
  __listItemCommon__?: 'common';
}

const SystemListItem = styledFactory<SystemListProps, 'li'>(
  'li',
  'ListItem',
  [
    variantFactory('components.listItem', {
      prop: '__listItemCommon__',
    }),
    variantFactory('components.listItem.bulleted', {
      prop: '__bulletCommon__',
    }),
    variantFactory('components.listItem.bulleted.variants', {
      prop: '__bulletVariant__',
    }),
    variantFactory('components.listItem.numbered', {
      prop: '__numberCommon__',
    }),
    variantFactory('components.listItem.numbered.variants', {
      prop: '__numberVariant__',
    }),
    variantFactory('components.listItem.dividedVertical', {
      prop: '__dividedVerticalCommon__',
    }),
    variantFactory('components.listItem.dividedVertical.variants', {
      prop: '__dividedVertical__',
    }),
    variantFactory('components.listItem.dividedHorizontal', {
      prop: '__dividedHorizontalCommon__',
    }),
    variantFactory('components.listItem.dividedHorizontal.variants', {
      prop: '__dividedHorizontal__',
    }),
    variantFactory('components.listItem.variants'),
  ],
  [
    border,
    textDecoration,
    textRendering,
    textTransform,
    typography,
    userSelect,
    borderShorthandOverride,
  ],
);

export type Props = {
  variant?: string;
} & React.ComponentPropsWithRef<typeof SystemListItem>;

const ListItem = forwardRef<HTMLLIElement, Props>(
  ({ variant = 'default', ...props }, ref) => {
    const itemProps = useContext(ListContext);

    return (
      <SystemListItem
        ref={ref}
        __listItemCommon__="common"
        {...itemProps}
        variant={variant}
        {...props}
      />
    );
  },
);

export default ListItem;
